import React from 'react';
import { ConnectionState, StreamMetadata } from '../types';

interface PlayerControlsProps {
  connectionState: ConnectionState;
  metadata: StreamMetadata | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onPause: () => void;
  onResume: () => void;
  canControl: boolean;
  isPaused: boolean;
  volume: number;
  muted: boolean;
  onVolumeChange: (value: number) => void;
  onToggleMute: () => void;
  selectedStreamId: string | null;
  audioFormat: string;
  onAudioFormatChange: (format: string) => void;
  isTextFormat?: boolean;
}

const renderMetadata = (metadata: StreamMetadata | null) => {
  if (!metadata) {
    return <p className="muted-text">Waiting for metadata...</p>;
  }

  return (
    <div className="metadata-grid">
      <div>
        <span className="label">Sample Rate</span>
        <strong>{metadata.sampleRate ?? 'Unknown'} Hz</strong>
      </div>
      <div>
        <span className="label">Channels</span>
        <strong>{metadata.channels ?? 'Unknown'}</strong>
      </div>
      <div>
        <span className="label">Codec</span>
        <strong>{metadata.codec ?? 'opus'}</strong>
      </div>
      <div>
        <span className="label">Bitrate</span>
        <strong>{metadata.bitrate ?? '--'} bps</strong>
      </div>
      {metadata.callInfo && (
        <>
          <div>
            <span className="label">Phone Number</span>
            <strong>{metadata.callInfo.phoneNumber ?? '--'}</strong>
          </div>
          <div>
            <span className="label">Direction</span>
            <strong>{metadata.callInfo.direction ?? '--'}</strong>
          </div>
        </>
      )}
    </div>
  );
};

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  connectionState,
  metadata,
  onConnect,
  onDisconnect,
  onPause,
  onResume,
  canControl,
  isPaused,
  volume,
  muted,
  onVolumeChange,
  onToggleMute,
  selectedStreamId,
  audioFormat,
  onAudioFormatChange,
  isTextFormat = false,
}) => {
  const connected = connectionState === 'connected';
  const connecting = connectionState === 'connecting' || connectionState === 'reconnecting';

  return (
    <div className="player-card">
      <div className="card-header">
        <div>
          <h2>Stream Player</h2>
          <p className="card-subtitle">
            WebSocket Subscribe + {audioFormat.toUpperCase()} {isTextFormat ? 'Text Processing' : audioFormat === 'opus' ? 'Decode + Web Audio Playback' : 'Web Audio Playback'}
          </p>
        </div>
        <div className={`connection-state ${connectionState}`}>{connectionState}</div>
      </div>

      <div className="player-actions">
        <div className="format-info">
          <span className="format-label">Current Format:</span>
          <span className={`format-badge ${isTextFormat ? 'text-format' : 'audio-format'}`}>
            {audioFormat.toUpperCase()}
            {isTextFormat ? ' (Text/STT)' : ' (Audio)'}
          </span>
          {connected && (
            <span className="format-note">Disconnect to change format</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="primary-button"
            onClick={connected ? onDisconnect : onConnect}
            disabled={connecting || (!selectedStreamId && !connected)}
          >
            {connected ? 'Disconnect' : connecting ? 'Connecting...' : 'Connect & Play'}
          </button>
          <button className="ghost-button" onClick={isPaused ? onResume : onPause} disabled={!connected}>
            {isPaused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>

      {!isTextFormat && (
        <div className="player-controls">
          <label htmlFor="volume">Volume</label>
          <input
            id="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            disabled={!canControl}
          />
          <span className="volume-value">{Math.round(volume * 100)}%</span>
          <button className="ghost-button" onClick={onToggleMute} disabled={!canControl}>
            {muted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      )}

      {isTextFormat && (
        <div className="text-controls">
          <p className="text-info">
            📝 Text stream - transcription will appear below when available
          </p>
        </div>
      )}

      <div className="metadata-card">
        <div className="card-header">
          <h3>Stream Metadata</h3>
          <p className="card-subtitle">StreamStart message from push client</p>
        </div>
        {renderMetadata(metadata)}
      </div>
    </div>
  );
};

